import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { QEStrategydataFlowContext } from "../../../main-pages/QEStrategyMainPage";
import QEStrategyCommonButton from "../qe-strategy-homepage/CommonButton";
import { usePrefillForm } from "../../../../utils/qe-strategy-utils/usePreFillForm";

const AIMLLLMConsiderations = ({ onBack, onNext }) => {
  // --- State Variables ---
  const [hasAiMl, setHasAiMl] = useState("");
  const [modelRetrain, setModelRetrain] = useState("");
  const [aiRisks, setAiRisks] = useState("");
  const [promptControl, setPromptControl] = useState("");
  const [hitlValidation, setHitlValidation] = useState("");
  const [datasetAudit, setDatasetAudit] = useState("");
  const [fairnessMetrics, setFairnessMetrics] = useState("");
  const { aiMLLLMConsideration, setAiMLLLMConsideration, dataFromFileUploads } =
    useContext(QEStrategydataFlowContext);

  // --- Handlers ---
  const handleHasAiMlChange = (e) => setHasAiMl(e.target.value);
  const handleModelRetrainChange = (e) => setModelRetrain(e.target.value);
  const handleAiRisksChange = (e) => setAiRisks(e.target.value);
  const handlePromptControlChange = (e) => setPromptControl(e.target.value);
  const handleHitlValidationChange = (e) => setHitlValidation(e.target.value);
  const handleDatasetAuditChange = (e) => setDatasetAudit(e.target.value);
  const handleFairnessMetricsChange = (e) => setFairnessMetrics(e.target.value);

  
  useEffect(() => {
    if (aiMLLLMConsideration) {
      setHasAiMl(aiMLLLMConsideration.hasAiMl || "");
      setModelRetrain(aiMLLLMConsideration.modelRetrain || "");
      setAiRisks(aiMLLLMConsideration.aiRisks || "");
      setPromptControl(aiMLLLMConsideration.promptControl || "");
      setHitlValidation(aiMLLLMConsideration.hitlValidation || "");
      setDatasetAudit(aiMLLLMConsideration.datasetAudit || "");
      setFairnessMetrics(aiMLLLMConsideration.fairnessMetrics || "");
    }
  }, [aiMLLLMConsideration]);

  const aIMLLLMConsiderationsdataobject = {
    hasAiMl,
    modelRetrain,
    aiRisks,
    promptControl,
    hitlValidation,
    datasetAudit,
    fairnessMetrics,
  };

  const [hasAiMlOptions, setHasAiMlOptions] = useState([
    "Yes–AI/ML",
    "Yes–LLM",
    "Yes–Both",
    "No",
  ]);

  const [aiRisksOptions, setAiRisksOptions] = useState([
    "Bias",
    "Drift",
    "Hallucination",
    "Non-determinism",
    "Ethics",
  ]);

  const [promptControlOptions, setPromptControlOptions] = useState([
    "Yes",
    "No",
  ]);

  const questionToFieldMap = {
    "1. Does the application include AI/ML or LLM components?": "hasAiMl",
    "2. Are models retrained after deployment?": "modelRetrain",
    "3. What risks apply to AI components?": "aiRisks",
    "4. Are prompts tested and version-controlled?": "promptControl",
    "5. Is Human-in-the-Loop (HITL) validation used for AI decisions?":
      "hitlValidation",
    "6. Are datasets audited for regulatory or ethical bias?": "datasetAudit",
    "7. Are fairness or explainability metrics part of quality gates?":
      "fairnessMetrics",
  };

  usePrefillForm(
    dataFromFileUploads,
    questionToFieldMap,
    {
      hasAiMl: setHasAiMl,
      modelRetrain: setModelRetrain,
      aiRisks: setAiRisks,
      promptControl: setPromptControl,
      hitlValidation: setHitlValidation,
      datasetAudit: setDatasetAudit,
      fairnessMetrics: setFairnessMetrics,
    },
    null,
    {
      hasAiMl: { options: hasAiMlOptions, setOptions: setHasAiMlOptions },
      aiRisks: { options: aiRisksOptions, setOptions: setAiRisksOptions },
      promptControl: {
        options: promptControlOptions,
        setOptions: setPromptControlOptions,
      },
    }
  );

  const handleBack = () => {
    onBack();
  };

  const handleNext = () => {
    setAiMLLLMConsideration(aIMLLLMConsiderationsdataobject);
    onNext();
  };
  return (
    <Box
      sx={{
        width: {
          xs: "100%", // full width on small screens
          sm: "100%",
          md: "1030px", // fixed width on medium and above
        },
        mx: "auto", // center horizontally
        mt: { xs: 5, sm: 8, md: 3 },
        mb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 }, // side padding only for smaller screens
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Title */}
        <Typography variant="h6" gutterBottom sx={{ fontSize: 20, position: 'relative', top: '-12px'}}>
          AI/ML/LLM Considerations
        </Typography>

        <Grid container spacing={3}>
          {/* Does the application include AI/ML/LLM? */}
          <Grid item xs={12}>
            <FormControl fullWidth  variant="outlined">
              <InputLabel
                shrink
                id="ai-ml-label"
                sx={{
                  background: "#fff",
                  px: 0.5,
                  mb: 0.5,
                }}
              >
                Does the application include AI/ML or LLM components?
              </InputLabel>
              <Select
                labelId="ai-ml-label"
                value={hasAiMl}
                onChange={handleHasAiMlChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#aaa" }}>Select</span>;
                  }
                  return selected;
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200, // ✅ scrollbar if too tall
                      width: 300,     // ✅ fixed width 300px
                    },
                  },
                  disablePortal: false,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PopperProps: {
                    modifiers: [
                      {
                        name: "flip",
                        enabled: false,
                      },
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: {
                          boundary: "viewport",
                        },
                      },
                    ],
                  },
                }}
              >
                {hasAiMlOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Models retrained after deployment */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>Are models retrained after deployment?</FormLabel>
              <RadioGroup
                row
                value={modelRetrain}
                onChange={handleModelRetrainChange}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Risks apply to AI components + Prompts tested */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth  variant="outlined">
              <InputLabel
                shrink
                id="ai-risks-label"
                sx={{
                  background: "#fff",
                  px: 0.5,
                  mb: 0.5,
                }}
              >
                What risks apply to AI components?
              </InputLabel>
              <Select
                labelId="ai-risks-label"
                value={aiRisks}
                onChange={handleAiRisksChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#aaa" }}>Select</span>;
                  }
                  return selected;
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200, // ✅ scrollbar if too tall
                      width: 300,     // ✅ fixed width 300px
                    },
                  },
                  disablePortal: false,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PopperProps: {
                    modifiers: [
                      {
                        name: "flip",
                        enabled: false,
                      },
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: {
                          boundary: "viewport",
                        },
                      },
                    ],
                  },
                }}
              >
                {aiRisksOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth  variant="outlined">
              <InputLabel
                shrink
                id="prompt-control-label"
                sx={{
                  background: "#fff",
                  px: 0.5,
                  mb: 0.5,
                }}
              >
                Are prompts tested and version-controlled?
              </InputLabel>
              <Select
                labelId="prompt-control-label"
                value={promptControl}
                onChange={handlePromptControlChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: "#aaa" }}>Select</span>;
                  }
                  return selected;
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200, // ✅ scrollbar if too tall
                      width: 300,     // ✅ fixed width 300px
                    },
                  },
                  disablePortal: false,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PopperProps: {
                    modifiers: [
                      {
                        name: "flip",
                        enabled: false,
                      },
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: {
                          boundary: "viewport",
                        },
                      },
                    ],
                  },
                }}
              >
                {promptControlOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* HITL Validation */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Is Human-in-the-Loop (HITL) validation used for AI decisions?
              </FormLabel>
              <RadioGroup
                row
                value={hitlValidation}
                onChange={handleHitlValidationChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Dataset Audit */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are datasets audited for regulatory or ethical bias?
              </FormLabel>
              <RadioGroup
                row
                value={datasetAudit}
                onChange={handleDatasetAuditChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Fairness Metrics */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are fairness or explainability metrics part of quality gates?
              </FormLabel>
              <RadioGroup
                row
                value={fairnessMetrics}
                onChange={handleFairnessMetricsChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Buttons outside Paper */}
      <QEStrategyCommonButton handleBack={handleBack} handleNext={handleNext} />
    </Box>
  );
};

export default AIMLLLMConsiderations;
